import { createContext, useMemo, useState, useCallback, useEffect, useContext } from 'react';
import * as usersApi from '../Api/Users';
import * as loginAttemptApi from '../Api/loginAttempt';
import * as api from "../Api";

import config from '../config.json';
import React from 'react';
import {Buffer} from 'buffer';
const JWT_TOKEN_KEY = config.token_key;

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

function parseJwt(token) {
  if (!token) return {};
  const base64Url = token.split('.')[1];
  const payload = Buffer.from(base64Url, 'base64');
  const jsonPayload = payload.toString('ascii');
  return JSON.parse(jsonPayload);
}

function parseExp(exp) {
  if (!exp) return null;
  if (typeof exp !== 'number') exp = Number(exp);
  if (isNaN(exp)) return null;
  return new Date(exp * 1000);
}


export const useSession = () => {
  const { token, user, ready, loading, error, hasRole } = useAuth();
  return {
    token,
    user,
    ready,
    error,
    loading,
    isAuthed: Boolean(token),
    hasRole,
  };
};

export const useLogin = () => {
  const { login } = useAuth();
  return login;
};

export const useLogout = () => {
  const { logout } = useAuth();
  return logout;
};

export const useSuccesfullLogin = ()=>{
  const {succesfullLogin} = useAuth();
  return succesfullLogin;
}

export const useFailedLogin = ()=>{
  const {failedLogin} = useAuth();
  return failedLogin;
}

export const AuthProvider = ({
  children,
}) => {
  // will be false until the token is set
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState(localStorage.getItem(JWT_TOKEN_KEY));
  const [user, setUser] = useState(null);

  const setSession = useCallback(async (token,user) => {
    const { exp, userId } = parseJwt(token);
    const expiry = parseExp(exp);
    const stillValid = expiry >= new Date();
    if (stillValid) {
      localStorage.setItem(JWT_TOKEN_KEY, token);
      localStorage.setItem("userID",userId)
    } else {
      localStorage.removeItem(JWT_TOKEN_KEY);
      localStorage.removeItem("userID")
      localStorage.removeItem("templateID")
      token = null;
    }
    if (!user && token != null && stillValid) {
      user = await usersApi.getUserById(userId);
      user = user.user
      localStorage.setItem("templateID",user.role.templateID)
    }
    api.setAuthToken(token);
    setUser(user);
    setToken(token);
    setReady(token && stillValid);
  }, []);
  useEffect(() => {
    setSession(token);
  }, [token, setSession]);

  const login = useCallback(async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      setSession(null, null);
      const { token, user } = await usersApi.login(username, password);
      await setSession(token, user);
      localStorage.setItem("role", user.role.rolename)
      return user;
    } catch (error) {
      setError('Login failed, try again');
      return false
    } finally {
      setLoading(false);
    }
  }, [setSession]);

  const logout = useCallback(() => {
    setSession(null, null);
    localStorage.removeItem("role")
  }, [setSession]);

  const hasRole = useCallback((role) => {
    if (!user) return false;
    return user.roles.includes(role);
  }, [user]);

  const succesfullLogin = useCallback((user) => {
    try {
      loginAttemptApi.succesfullLogin(user.username, user.role.rolename);
    } catch (error) {
      setError('logging login Attempt failed');
    }
  }, []);

  const failedLogin = useCallback((username,role, attempt, status) => {
    try {
      loginAttemptApi.failedLogin(username, role, attempt, status);
    } catch (error) {
      setError('logging login Attempt failed');
    }
  }, []);

  const value = useMemo(() => ({
    token,
    user,
    ready,
    loading,
    error,
    login,
    logout,
    hasRole,
    succesfullLogin,
    failedLogin
  }), [token, user, ready, loading, error, login, logout, hasRole, succesfullLogin, failedLogin]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};